import {
  type FeedbackViewRestore,
  type FeedbackViewSeedResult,
  readViewFlag,
  readViewRecord,
  readViewSelection,
  readViewString,
  readViewStrings,
  restoreDecision,
  useFeedbackViewRestore,
  useRegisterFeedbackView
} from "@appretio/feedback";
import { useLatestRef } from "@appretio/shared";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  type IntakeSource,
  isIntakeSource
} from "../components/cubeDataTable/utils/intakeUtils";
import { resolveStoreStatusToLocations } from "../hooks/useFetchStoreStatusOptions";
import { budgetListKey } from "../hooks/useScopedBudgetVersions";
import type { SummaryPageState } from "../hooks/useSummaryPageState";
import { summaryBudgetBrand } from "../hooks/useSummaryPageState/useSummarySourceSelection";
import { parseSummaryViewPayload } from "../summaryViews/summaryViewPayload";
import { contextOffers } from "../utils/pruneStaleContextSelections";

/** The source picks the summary's source bar holds, with their labels. */
const SOURCE_LABELS = {
  producer: "producer",
  version: "version",
  year: "year",
  idxProducer: "index producer",
  idxVersion: "index version",
  idxYear: "index year"
} as const;

type SourceKey = keyof typeof SOURCE_LABELS;

const SOURCE_KEYS = Object.keys(SOURCE_LABELS) as SourceKey[];

/** The reporter's source picks, as the seed wrote them. */
type SourcePicks = Record<SourceKey, string>;

/** The setter of each source pick, by key. */
function sourceSetters(
  state: SummaryPageState
): Record<SourceKey, (value: string) => void> {
  return {
    producer: state.setProducer,
    version: state.setVersion,
    year: state.setYear,
    idxProducer: state.setIdxProducer,
    idxVersion: state.setIdxVersion,
    idxYear: state.setIdxYear
  };
}

/** Store statuses are selected but resolve to no store locations (yet). */
function storeFilterPending(state: SummaryPageState): boolean {
  return (
    state.selectedStoreStatuses.length > 0 &&
    state.resolvedStoreLocations.length === 0
  );
}

/**
 * Whether the lists the page loads after the seed offer its picks — the
 * version under its producer, the year on its cube, the context values in
 * the catalogue — and the store statuses have resolved to locations.
 */
function isSeedConfirmed(state: SummaryPageState): boolean {
  return (
    isSourceConfirmed(state) &&
    !state.baseLoading &&
    Object.entries(state.selectedContextOptions ?? {}).every(
      ([field, values]) =>
        (values ?? []).every((value) =>
          contextOffers(state.offeredContext, field, value)
        )
    ) &&
    !storeFilterPending(state)
  );
}

/** Whether the source bar holds a complete source the lists offer. */
function isSourceConfirmed(state: SummaryPageState): boolean {
  return (
    state.missingSources.length === 0 &&
    state.selectedVersion !== undefined &&
    state.selectedYear !== undefined &&
    (!state.idxVersion ||
      (state.selectedIdxVersion !== undefined &&
        state.selectedIdxYear !== undefined))
  );
}

/** The reporter's source picks and context selection, as the seed read them. */
interface Seeded {
  picks: SourcePicks;
  /** Undefined when the report holds no selection. */
  selection?: Record<string, string[]>;
  storeStatuses: string[];
  /**
   * The picks name each cube under the producer that lists it to this viewer,
   * and the page holds them. False while the version list loads.
   */
  ownersResolved: boolean;
}

/**
 * The picks with each user cube under the producer that lists it to this
 * viewer, or undefined while the version list loads. `user` lists the
 * viewer's own cubes, so another user's report names theirs `user` too.
 */
function viewerPicks(
  picks: SourcePicks,
  isVersionOffered: SummaryPageState["isVersionOffered"]
): SourcePicks | undefined {
  const otherOwner: Partial<Record<string, string>> = {
    user: "other_user",
    other_user: "user"
  };
  const viewer = { ...picks };
  for (const [producerKey, versionKey] of [
    ["producer", "version"],
    ["idxProducer", "idxVersion"]
  ] satisfies [SourceKey, SourceKey][]) {
    const other = otherOwner[picks[producerKey]];
    const version = picks[versionKey];
    if (!other || !version) continue;
    const offered = isVersionOffered(picks[producerKey], version);
    if (offered === undefined) return undefined;
    if (!offered && isVersionOffered(other, version)) {
      viewer[producerKey] = other;
    }
  }
  return viewer;
}

/** Writes the source picks through the page's setters. */
function setSourcePicks(state: SummaryPageState, picks: SourcePicks) {
  const setters = sourceSetters(state);
  for (const key of SOURCE_KEYS) setters[key](picks[key]);
}

/**
 * Lands the reporter's source picks once the version list says which
 * producer lists each user cube to this viewer, while the banner is up.
 * Landing them in one write lets the version/year sync check them as a
 * user's pick. The selection lands with them, so the prune checks it
 * against their cube's catalogue.
 */
function useViewerOwnedPicks(
  state: SummaryPageState,
  restoring: boolean,
  seeded: Seeded | null,
  setSeeded: (seeded: Seeded) => void
) {
  const stateRef = useLatestRef(state);
  const { isVersionOffered } = state;
  useEffect(() => {
    if (!restoring || !seeded || seeded.ownersResolved) return;
    const picks = viewerPicks(seeded.picks, isVersionOffered);
    if (!picks) return;
    setSeeded({ ...seeded, picks, ownersResolved: true });
    setSourcePicks(stateRef.current, picks);
    if (seeded.selection) {
      stateRef.current.setSelectedContextOptions(seeded.selection);
    }
  }, [restoring, seeded, isVersionOffered, setSeeded, stateRef]);
}

/**
 * Whether the list that answers a seeded cube lacks it. A budget cube is
 * answered only by the budget list the page loaded for the seed's own key:
 * not while the scope loads, and not from a list for another brand.
 */
function versionGone(
  state: SummaryPageState,
  seeded: Seeded,
  producer: string,
  version: string
): boolean {
  if (!version) return false;
  if (producer !== "budget") {
    return state.isVersionOffered(producer, version) === false;
  }
  const seedKey = budgetListKey(
    state.budgetScope,
    summaryBudgetBrand(seeded.selection)
  );
  const budgets = state.budgetVersionsData?.budget_versions;
  return (
    seedKey !== undefined &&
    seedKey === state.budgetKey &&
    !!budgets &&
    !budgets.some((budget) => budget.cube_id === version)
  );
}

/**
 * Whether the seeded store statuses resolve to no store. Only a loaded,
 * non-empty status list answers it: a failed fetch reads as empty.
 */
function storeStatusesGone(state: SummaryPageState, seeded: Seeded): boolean {
  const options = state.storeStatusOptions;
  return (
    seeded.storeStatuses.length > 0 &&
    !!options?.length &&
    resolveStoreStatusToLocations(seeded.storeStatuses, options).length === 0
  );
}

/**
 * Whether the year list of the seeded cube lacks the seeded year. Only that
 * cube's list answers it, so not while the page holds another cube.
 */
function yearGone(
  year: string,
  version: string,
  heldVersion: string,
  yearOptions: { value: string }[]
): boolean {
  return (
    !!year &&
    version === heldVersion &&
    yearOptions.length > 0 &&
    !yearOptions.some((option) => option.value === year)
  );
}

/**
 * What of the seeded picks the lists that answer them no longer offer, as a
 * sentence, or null. Never read from the live selection differing from the
 * seed: that may be the viewer's own change.
 */
function goneFromSeed(
  state: SummaryPageState,
  seeded: Seeded | null
): string | null {
  if (!seeded?.ownersResolved) return null;
  const { picks } = seeded;
  const goneSource = [
    versionGone(state, seeded, picks.producer, picks.version) && "version",
    yearGone(picks.year, picks.version, state.version, state.yearOptions) &&
      "year",
    versionGone(state, seeded, picks.idxProducer, picks.idxVersion) &&
      "idxVersion",
    yearGone(
      picks.idxYear,
      picks.idxVersion,
      state.idxVersion,
      state.idxYearOptions
    ) && "idxYear"
  ]
    .filter((key): key is SourceKey => typeof key === "string")
    .map((key) => `${SOURCE_LABELS[key]} ${picks[key]}`);
  // The catalogue answers the seeded context only while it is the seeded
  // cube's.
  const catalogueAnswers =
    !state.baseLoading && state.version === picks.version;
  const goneContext = catalogueAnswers
    ? Object.entries(seeded.selection ?? {}).flatMap(([field, values]) =>
        values
          .filter((value) => !contextOffers(state.offeredContext, field, value))
          .map((value) => `${field} ${value}`)
      )
    : [];
  const sentences = [
    goneSource.length > 0 &&
      `The reporter's source is no longer available: ${goneSource.join(", ")}.`,
    goneContext.length > 0 &&
      `The reporter's context is no longer available: ${goneContext.join(", ")}.`,
    storeStatusesGone(state, seeded) &&
      "The reporter's store statuses do not resolve to any store, so their store filter cannot be applied."
  ].filter((sentence) => typeof sentence === "string");
  return sentences.length > 0 ? sentences.join(" ") : null;
}

/** What a restore sets only once its own Apply has loaded. */
interface AfterLoad {
  intake?: IntakeSource;
  graphMetric?: string;
  /** The graph's table, by its label: indexes do not survive a reload. */
  graphTable?: string;
}

/**
 * Lands the reporter's intake and graph picks once the restore's own Apply
 * has loaded, or notes the ones the loaded data does not offer.
 */
function useAfterRestoredLoad(
  state: SummaryPageState,
  restoring: boolean,
  afterLoad: AfterLoad | null,
  setAfterLoad: (afterLoad: AfterLoad | null) => void,
  addNote: (note: string) => void
) {
  const { grid, tableLabels } = state;
  const addNoteRef = useLatestRef(addNote);
  useEffect(() => {
    if (!afterLoad) return;
    setAfterLoad(null);
    if (!restoring) return;
    const { intake, graphMetric, graphTable } = afterLoad;
    // The load opens on the tenant's intake source; the reporter's may differ.
    if (intake && intake !== grid.intakeMode) {
      if (grid.availableIntakeSources.includes(intake)) {
        grid.handleIntakeToggle(intake);
      } else {
        addNoteRef.current(
          `The reporter was viewing the ${intake} intake, which the loaded data does not offer.`
        );
      }
    }
    if (
      graphMetric &&
      grid.graphMetricOptions.some((option) => option.value === graphMetric)
    ) {
      grid.setGraphMetric(graphMetric);
    } else if (graphMetric) {
      addNoteRef.current(
        `The reporter's graph showed ${graphMetric}, which the loaded data does not offer.`
      );
    }
    const tableIndex = graphTable ? tableLabels.indexOf(graphTable) : -1;
    if (tableIndex >= 0) grid.setGraphTableIndex(tableIndex);
    else if (graphTable) {
      addNoteRef.current(
        `The reporter's graph showed the ${graphTable} table, which the loaded data does not offer.`
      );
    }
  }, [afterLoad, restoring, grid, tableLabels, setAfterLoad, addNoteRef]);
}

/**
 * Feedback capture and restore for the forecast and budget summary pages.
 * A report from before this capture restores the context selection only. The
 * reporter's grid edits are never restored.
 */
export function useSummaryFeedbackRestore(state: SummaryPageState) {
  const location = useLocation();
  const navigate = useNavigate();
  const [notes, setNotes] = useState<string[]>([]);
  const [seeded, setSeeded] = useState<Seeded | null>(null);
  const [seededAfterLoad, setSeededAfterLoad] = useState<AfterLoad>({});
  const [afterLoad, setAfterLoad] = useState<AfterLoad | null>(null);

  useRegisterFeedbackView(() => ({
    selection: state.selectedContextOptions,
    store_statuses: state.selectedStoreStatuses,
    source: Object.fromEntries(SOURCE_KEYS.map((key) => [key, state[key]])),
    display: state.views.capturePayload(),
    intake: state.grid.intakeMode,
    graph_visible: state.grid.graphVisible,
    graph: {
      metric: state.grid.graphMetric,
      table: state.tableLabels[state.grid.graphTableIndex] ?? null
    },
    applied: state.appliedFilters !== null,
    selector_dirty: state.isContextDirty,
    unsaved_edits: state.grid.hasUnsavedEdits
  }));

  function seed(restore: FeedbackViewRestore): FeedbackViewSeedResult {
    const { view } = restore;
    const viewSelection = readViewSelection(view?.selection);
    const selection = viewSelection ?? restore.selectorDraft;
    const source = readViewRecord(view?.source) ?? {};
    const reported = Object.fromEntries(
      SOURCE_KEYS.map((key) => [key, readViewString(source[key]) ?? ""])
    ) as SourcePicks;
    const picks = view
      ? viewerPicks(reported, state.isVersionOffered)
      : undefined;
    if (selection) {
      // Until the picks land, the catalogue is another cube's: its prune
      // would drop values only the reporter's cube offers.
      if (!view || picks) state.setSelectedContextOptions(selection);
      state.contextViews.markOpened();
      state.contextViews.clearActiveView();
    }
    if (!view) return { apply: false };

    const storeStatuses = readViewStrings(view.store_statuses);
    state.setSelectedStoreStatuses(storeStatuses);
    if (picks) setSourcePicks(state, picks);
    setSeeded({
      picks: picks ?? reported,
      selection: selection ?? undefined,
      storeStatuses,
      ownersResolved: picks !== undefined
    });
    state.markMainCubeOpened();
    state.views.markOpened();
    const display = parseSummaryViewPayload(JSON.stringify(view.display));
    if (display) {
      state.views.applyPayload(display);
      state.views.clearActiveView();
    }
    const graphVisible = readViewFlag(view.graph_visible);
    if (graphVisible !== undefined) state.grid.setGraphVisible(graphVisible);

    const intake = readViewString(view.intake);
    const graph = readViewRecord(view.graph);
    setSeededAfterLoad({
      intake: intake && isIntakeSource(intake) ? intake : undefined,
      graphMetric: graphVisible ? readViewString(graph?.metric) : undefined,
      graphTable: graphVisible ? readViewString(graph?.table) : undefined
    });
    const decision = restoreDecision(view, {
      data: "data",
      edits: "grid",
      hasUnsavedEdits: state.grid.hasUnsavedEdits
    });
    setNotes(decision.notes);
    return { apply: decision.apply, selectionRestored: viewSelection !== null };
  }

  async function applyRestored() {
    const loaded = await state.handleApply();
    if (loaded) setAfterLoad(seededAfterLoad);
  }

  const { restore, applyNote, dismiss } = useFeedbackViewRestore({
    location,
    navigate,
    seed,
    apply: () => void applyRestored(),
    readyToApply: !!seeded?.ownersResolved && isSeedConfirmed(state),
    unavailable: goneFromSeed(state, seeded)
  });
  useViewerOwnedPicks(state, restore !== null, seeded, setSeeded);

  useAfterRestoredLoad(
    state,
    restore !== null,
    afterLoad,
    setAfterLoad,
    (note) => setNotes((current) => [...current, note])
  );

  return { restore, notes, applyNote, dismiss };
}
